import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasAccess } from "@/lib/roles";
import { Plus, Trash2, Save, X, Clock, MoonStar } from "lucide-react";

const SHIFT_TYPES = ["opening", "mid", "closing", "custom"];
const DEPARTMENTS = ["Floor", "Cashier", "Stock", "Security", "Cleaning", "Management"];

function calcHours(start, end, brk) {
  try {
    const [h1, m1] = String(start).split(":").map(Number);
    const [h2, m2] = String(end).split(":").map(Number);
    const s = h1 * 60 + m1;
    let e = h2 * 60 + m2;
    if (e <= s) e += 24 * 60;
    const mins = Math.max(0, e - s - (Number(brk) || 0));
    return mins / 60;
  } catch {
    return 0;
  }
}

function displayEnd(end) {
  return end === "00:00" ? "24:00 (midnight)" : end;
}

const emptyForm = () => ({
  name: "",
  start_time: "10:00",
  end_time: "18:00",
  break_minutes: 30,
  role: "Floor",
  department: "Floor",
  shift_type: "mid",
  notes: "",
  active: true,
});

export default function ShiftTemplates() {
  const { user } = useAuth();
  const canEdit = hasAccess(user?.role, ["admin", "gm", "manager"]);

  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // null | { id?, ...form }

  const load = useCallback(() => {
    api.get("/shift_templates").then(({ data }) => setTemplates(data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    try {
      if (form.id) {
        const { id, ...body } = form;
        await api.patch(`/shift_templates/${id}`, body);
        toast.success("Template updated");
      } else {
        await api.post("/shift_templates", form);
        toast.success("Template created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.delete(`/shift_templates/${t.id}`);
      toast.success("Template deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="shift-templates-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-caps">Workforce</div>
          <h1 className="font-heading text-3xl sm:text-4xl">Shift Templates</h1>
          <p className="text-sm text-[color:var(--text-secondary)] mt-1 max-w-2xl">
            Reusable shift patterns (e.g. Opening 10–18, Closing 18–24). Apply them to selected employees × days from the Schedule Builder.
          </p>
        </div>
        {canEdit && (
          <button className="hos-btn-primary flex items-center gap-2" onClick={() => setEditing(emptyForm())} data-testid="new-template-btn">
            <Plus size={16}/> New Template
          </button>
        )}
      </div>

      <div className="hos-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left label-caps">
            <tr>
              <th className="p-3">Name</th>
              <th>Start</th>
              <th>End</th>
              <th>Break</th>
              <th>Hours</th>
              <th>Role</th>
              <th>Dept</th>
              <th>Type</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {templates.map((t) => (
              <tr key={t.id} className="border-t border-[color:var(--border)]" data-testid={`template-row-${t.id}`}>
                <td className="p-3">
                  <div className="font-semibold text-[color:var(--gold)]">{t.name}</div>
                  {t.notes && <div className="text-xs text-[color:var(--text-secondary)]">{t.notes}</div>}
                </td>
                <td>{t.start_time}</td>
                <td className="flex items-center gap-1 py-3">
                  {t.end_time === "00:00" && <MoonStar size={12} className="text-[color:var(--gold)]"/>}
                  {displayEnd(t.end_time)}
                </td>
                <td>{t.break_minutes}m</td>
                <td>{(t.hours ?? calcHours(t.start_time, t.end_time, t.break_minutes)).toFixed(1)}h</td>
                <td>{t.role}</td>
                <td>{t.department}</td>
                <td className="capitalize">{t.shift_type}</td>
                <td className="whitespace-nowrap">
                  {canEdit && (
                    <div className="flex gap-2 justify-end pr-3">
                      <button className="hos-btn-outline py-1 px-3" onClick={() => setEditing({ ...t })} data-testid={`edit-template-${t.id}`}>Edit</button>
                      <button className="text-[color:var(--error)] p-1" onClick={() => remove(t)} data-testid={`delete-template-${t.id}`}><Trash2 size={14}/></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {templates.length === 0 && (
              <tr><td colSpan={9} className="p-8 text-center text-[color:var(--text-secondary)]">No templates yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <TemplateForm
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

function TemplateForm({ initial, onClose, onSave }) {
  const [f, setF] = useState(initial);
  const set = (patch) => setF({ ...f, ...patch });
  const midnight = f.end_time === "00:00";
  const hrs = calcHours(f.start_time, f.end_time, f.break_minutes);

  const submit = () => {
    if (!f.name.trim()) return toast.error("Please provide a name");
    if (!f.start_time || !f.end_time) return toast.error("Start and end times are required");
    if (f.start_time === f.end_time) return toast.error("Start and end can't be the same");
    onSave(f);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <div className="hos-card p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="template-form-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-2xl">{f.id ? "Edit Template" : "New Shift Template"}</h3>
          <button onClick={onClose}><X size={18}/></button>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="col-span-2">
            <label className="label-caps">Name</label>
            <input className="hos-input" placeholder="e.g. Closing (18–24)" value={f.name} onChange={(e) => set({ name: e.target.value })} data-testid="template-name"/>
          </div>
          <div>
            <label className="label-caps">Start</label>
            <input type="time" className="hos-input" value={f.start_time} onChange={(e) => set({ start_time: e.target.value })} data-testid="template-start"/>
          </div>
          <div>
            <label className="label-caps flex items-center justify-between">
              End
              <label className="normal-case text-[10px] flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={midnight}
                  onChange={(e) => set({ end_time: e.target.checked ? "00:00" : "18:00" })}
                  data-testid="template-midnight-toggle"
                /> ends at midnight (24:00)
              </label>
            </label>
            <input type="time" className="hos-input" value={f.end_time} onChange={(e) => set({ end_time: e.target.value })} disabled={midnight} data-testid="template-end"/>
            {midnight && <div className="text-[11px] text-[color:var(--gold)] mt-1">End set to 24:00 (midnight)</div>}
          </div>
          <div>
            <label className="label-caps">Break (min)</label>
            <input type="number" className="hos-input" value={f.break_minutes} onChange={(e) => set({ break_minutes: Number(e.target.value) })}/>
          </div>
          <div>
            <label className="label-caps">Duration</label>
            <div className="hos-input flex items-center gap-2 opacity-80" data-testid="template-hours-preview"><Clock size={14}/>{hrs.toFixed(2)} h</div>
          </div>
          <div>
            <label className="label-caps">Role</label>
            <input className="hos-input" value={f.role || ""} onChange={(e) => set({ role: e.target.value })}/>
          </div>
          <div>
            <label className="label-caps">Department</label>
            <select className="hos-input" value={f.department || "Floor"} onChange={(e) => set({ department: e.target.value })}>
              {DEPARTMENTS.map((d) => <option key={d}>{d}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="label-caps">Shift Type</label>
            <select className="hos-input" value={f.shift_type || "mid"} onChange={(e) => set({ shift_type: e.target.value })}>
              {SHIFT_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="label-caps">Notes</label>
            <input className="hos-input" value={f.notes || ""} onChange={(e) => set({ notes: e.target.value })}/>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-5">
          <button className="hos-btn-outline" onClick={onClose}>Cancel</button>
          <button className="hos-btn-primary flex items-center gap-2" onClick={submit} data-testid="template-save-btn"><Save size={16}/> Save</button>
        </div>
      </div>
    </div>
  );
}
